import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

import { MainConfig } from '../../../conf/main.config';
import { ObservationsService } from '../../programs/observations/observations.service';
import {
    ObservationFeature,
    TaxonomyListItem,
} from '../../programs/observations/observation.model';

type CatalogView = 'families' | 'scientific' | 'common' | 'photos';

interface FamilyGroup {
    name: string;
    speciesCount: number;
    photoCount: number;
    observationCount: number;
    cover: string | null;
    order: string | null;
}

@Component({
    selector: 'app-species-list',
    templateUrl: './species-list.component.html',
    styleUrls: ['./species-list.component.css'],
})
export class SpeciesListComponent implements OnInit {
    MainConfig = MainConfig;
    currentLanguage = 'pt-BR';
    taxa: TaxonomyListItem[] = [];
    observations: ObservationFeature[] = [];
    query = '';
    family = '';
    photoFilter = '';
    viewMode: CatalogView = 'families';
    selectedFamily = '';
    loading = true;
    error = false;

    private pendingFamily = '';

    constructor(
        private http: HttpClient,
        private observationsService: ObservationsService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.currentLanguage =
            localStorage.getItem('language') === 'fr' ? 'fr' : 'pt-BR';
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            const requestedView = String(params['view'] || '').toLowerCase();
            this.viewMode = this.isCatalogView(requestedView)
                ? (requestedView as CatalogView)
                : 'families';

            this.photoFilter = params['photo'] === 'missing' ? 'missing' : '';
            this.pendingFamily = this.cleanFamilyParam(params['family']);

            if (this.taxa.length) {
                this.applyPendingFamily();
            }
        });

        this.http
            .get<TaxonomyListItem[]>(
                MainConfig.API_ENDPOINT + '/taxonomy/lists/101/species',
                { params: { limit: '300' } }
            )
            .subscribe(
                (taxa) => {
                    this.taxa = (taxa || []).filter(
                        (item) =>
                            !!item &&
                            !!item.taxref &&
                            Number.isFinite(Number(item.taxref.cd_nom))
                    );
                    this.applyPendingFamily();
                    this.loading = false;
                    this.error = false;
                },
                () => {
                    this.loading = false;
                    this.error = true;
                }
            );

        this.observationsService.getRecentObservations(500).subscribe(
            (result) => (this.observations = result.features || []),
            () => (this.observations = [])
        );
    }

    t(portuguese: string, french: string): string {
        return this.currentLanguage === 'fr' ? french : portuguese;
    }

    get families(): string[] {
        return Array.from(
            new Set(
                this.taxa
                    .map((item) => item.taxref && item.taxref.famille)
                    .filter((value) => !!value)
            )
        ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }

    get familyGroups(): FamilyGroup[] {
        return this.families.map((familyName) => {
            const items = this.taxa.filter(
                (item) =>
                    item &&
                    item.taxref &&
                    item.taxref.famille === familyName
            );
            const ids = new Set(items.map((item) => Number(item.taxref.cd_nom)));
            const observationCount = this.observations.filter(
                (observation) =>
                    observation &&
                    observation.properties &&
                    ids.has(Number(observation.properties.cd_nom))
            ).length;
            const photoCount = items.reduce(
                (total, item) =>
                    total + (item.medias && item.medias.length ? item.medias.length : 0),
                0
            );
            const coverItem = items.find((item) => !!this.getTaxonPhoto(item));

            return {
                name: familyName,
                speciesCount: items.length,
                photoCount,
                observationCount,
                cover: coverItem ? this.getTaxonPhoto(coverItem) : null,
                order:
                    (items.find((item) => item.taxref && item.taxref.ordre) || {
                        taxref: { ordre: null },
                    } as any).taxref.ordre || null,
            };
        });
    }

    get filteredTaxa(): TaxonomyListItem[] {
        const query = this.normalize(this.query).slice(0, 120);

        return this.taxa
            .filter((item) => {
                if (!item || !item.taxref) {
                    return false;
                }

                if (this.family && item.taxref.famille !== this.family) {
                    return false;
                }

                if (this.photoFilter === 'missing' && this.getTaxonPhoto(item)) {
                    return false;
                }

                if (!query) {
                    return true;
                }

                return [
                    item.taxref.nom_vern,
                    item.taxref.lb_nom,
                    item.taxref.famille,
                    item.taxref.sous_famille,
                ].some((value) => this.normalize(value).indexOf(query) >= 0);
            })
            .sort((a, b) => {
                const valueA =
                    this.viewMode === 'common'
                        ? a.taxref.nom_vern || a.taxref.lb_nom
                        : a.taxref.lb_nom || a.taxref.nom_vern;
                const valueB =
                    this.viewMode === 'common'
                        ? b.taxref.nom_vern || b.taxref.lb_nom
                        : b.taxref.lb_nom || b.taxref.nom_vern;
                return String(valueA || '').localeCompare(
                    String(valueB || ''),
                    'pt-BR'
                );
            });
    }

    get photoTaxa(): TaxonomyListItem[] {
        return this.filteredTaxa.filter((item) => !!this.getTaxonPhoto(item));
    }

    get selectedFamilyTaxa(): TaxonomyListItem[] {
        if (!this.selectedFamily) {
            return [];
        }

        return this.taxa
            .filter(
                (item) =>
                    item &&
                    item.taxref &&
                    item.taxref.famille === this.selectedFamily
            )
            .sort((a, b) =>
                String(a.taxref.lb_nom || '').localeCompare(
                    String(b.taxref.lb_nom || ''),
                    'pt-BR'
                )
            );
    }

    get selectedFamilyOrder(): string {
        const item = this.selectedFamilyTaxa.find(
            (taxon) => taxon.taxref && taxon.taxref.ordre
        );
        return item && item.taxref ? item.taxref.ordre : '';
    }

    get selectedFamilyObservationCount(): number {
        const ids = new Set(
            this.selectedFamilyTaxa.map((item) => Number(item.taxref.cd_nom))
        );

        return this.observations.filter(
            (observation) =>
                observation &&
                observation.properties &&
                ids.has(Number(observation.properties.cd_nom))
        ).length;
    }

    get selectedFamilyPhotoCount(): number {
        return this.selectedFamilyTaxa.reduce(
            (total, item) =>
                total + (item.medias && item.medias.length ? item.medias.length : 0),
            0
        );
    }

    get selectedFamilyPhotoTaxa(): TaxonomyListItem[] {
        return this.selectedFamilyTaxa
            .filter((item) => !!this.getTaxonPhoto(item))
            .slice(0, 8);
    }

    setView(mode: CatalogView): void {
        if (!this.isCatalogView(mode)) {
            return;
        }

        this.viewMode = mode;
        this.selectedFamily = '';
        this.family = '';
        this.photoFilter = '';

        this.router.navigate(['/species'], {
            queryParams: { view: mode },
        });
    }

    openFamily(familyName: string): void {
        const safeFamily = this.validateFamily(familyName);
        if (!safeFamily) {
            return;
        }

        this.viewMode = 'families';
        this.selectedFamily = safeFamily;
        this.family = safeFamily;
        this.photoFilter = '';

        this.router.navigate(['/species'], {
            queryParams: {
                view: 'families',
                family: safeFamily,
            },
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    closeFamily(): void {
        this.selectedFamily = '';
        this.family = '';
        this.pendingFamily = '';

        this.router.navigate(['/species'], {
            queryParams: { view: 'families' },
        });
    }

    showFamilyPhotos(): void {
        if (!this.selectedFamily) {
            return;
        }

        const safeFamily = this.validateFamily(this.selectedFamily);
        if (!safeFamily) {
            return;
        }

        this.viewMode = 'photos';
        this.family = safeFamily;

        this.router.navigate(['/species'], {
            queryParams: {
                view: 'photos',
                family: safeFamily,
            },
        });
    }

    onFamilyFilterChange(): void {
        this.selectedFamily = '';
        this.pendingFamily = '';
    }

    clearFilters(): void {
        this.query = '';
        this.family = '';
        this.selectedFamily = '';
        this.photoFilter = '';

        this.router.navigate(['/species'], {
            queryParams: { view: this.viewMode },
        });
    }

    openTaxon(item: TaxonomyListItem): void {
        if (!item || !item.taxref) {
            return;
        }

        const taxonId = Number(item.taxref.cd_nom);
        if (!Number.isFinite(taxonId) || taxonId <= 0) {
            return;
        }

        this.router.navigate(['/synthesis/species', taxonId]);
    }

    getTaxonPhoto(item: TaxonomyListItem): string {
        if (
            !item ||
            !item.taxref ||
            !item.medias ||
            !item.medias.length
        ) {
            return null;
        }

        const mediaId = Number(item.medias[0].id_media);
        if (!Number.isFinite(mediaId) || mediaId <= 0) {
            return null;
        }

        return (
            MainConfig.API_ENDPOINT +
            '/tmedias/thumbnail/' +
            mediaId +
            '?h=700'
        );
    }

    getFamilyDescription(): string {
        if (!this.selectedFamily) {
            return '';
        }

        const speciesCount = this.selectedFamilyTaxa.length;
        const observationCount = this.selectedFamilyObservationCount;
        const orderText = this.selectedFamilyOrder
            ? this.t(
                  ' Ela está classificada na ordem ' +
                      this.selectedFamilyOrder +
                      '.',
                  ' Elle est classée dans l’ordre ' +
                      this.selectedFamilyOrder +
                      '.'
              )
            : '';

        return (
            this.t(
                'A família ' +
                    this.selectedFamily +
                    ' reúne ' +
                    speciesCount +
                    (speciesCount === 1
                        ? ' espécie cadastrada'
                        : ' espécies cadastradas') +
                    ' na taxonomia atual do WikiPrimes.' +
                    orderText +
                    ' Nesta página você pode consultar os nomes científicos, nomes populares, fotos disponíveis e os registros publicados pela comunidade (' +
                    observationCount +
                    ').',
                'La famille ' +
                    this.selectedFamily +
                    ' regroupe ' +
                    speciesCount +
                    (speciesCount === 1
                        ? ' espèce enregistrée'
                        : ' espèces enregistrées') +
                    ' dans la taxonomie actuelle de WikiPrimes.' +
                    orderText +
                    ' Cette page présente les noms scientifiques, les noms communs, les photos disponibles et les observations publiées par la communauté (' +
                    observationCount +
                    ').'
            )
        );
    }

    private applyPendingFamily(): void {
        if (!this.pendingFamily) {
            this.selectedFamily = '';
            this.family = '';
            return;
        }

        const safeFamily = this.validateFamily(this.pendingFamily);
        this.selectedFamily = safeFamily;
        this.family = safeFamily;
    }

    private validateFamily(value: string): string {
        const cleaned = this.cleanFamilyParam(value);
        if (!cleaned) {
            return '';
        }

        return this.families.indexOf(cleaned) >= 0 ? cleaned : '';
    }

    private cleanFamilyParam(value: any): string {
        if (typeof value !== 'string') {
            return '';
        }

        return value.trim().slice(0, 80);
    }

    private isCatalogView(value: any): boolean {
        return (
            value === 'families' ||
            value === 'scientific' ||
            value === 'common' ||
            value === 'photos'
        );
    }

    private normalize(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }
}
